import { useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Confirmation_, PageFull } from 'monica-alexandria';
import {
    confirm_agency_offer_payment_by_id,
    confirm_offer_payment_by_id,
    delete_agency_offer_by_id,
    delete_offer_by_id,
    extend_agency_offer_validity_by_id,
    extend_offer_validity_by_id,
    get_agency_public_offer_by_id,
    get_public_offer_by_id,
    init_agency_offer_payment_by_id,
    init_offer_payment_by_id,
    track_offer_visit_by_id,
    track_agency_offer_visit_by_id
} from '../../../services/api/Offers';
import { useHrAccess } from '../../../services/authentication/HrAccess';
import { New_Offer_Preview_Page } from '../newOffer/components/New_Offer_Preview_Page';
import { offer_reference_from_offer } from '../../../helpers/offerObject';
import { can_view_public_offer } from '../../../helpers/offerAccess';
import { checkPermissions } from '../../../helpers/checkPermissions';
import Offer_Preview_Admin_Actions from './components/Offer_Preview_Admin_Actions';
import Offer_Bank_Transfer from '../../../components/offerPayment/Offer_Bank_Transfer';
import Offer_Preview_Export_Action from './components/Offer_Preview_Export_Action';
import Offer_Preview_Paid_Slider from './components/Offer_Preview_Paid_Slider';
import Offer_Preview_Payment from './components/Offer_Preview_Payment';
import {
    build_payment_confirmation,
    build_payment_overview,
    can_mark_agency_offer_paid,
    can_view_offer_preview_internal_ui,
    initial_offer_payment_recording,
    initialised_payment_types_from_offer,
    mark_visit_recorded_for_session,
    offer_can_extend,
    offer_is_unavailable,
    offer_payment_recording_reducer,
    offerPreviewCopyByLanguage,
    payment_recording_context_from_offer,
    read_cerebro_connection_message,
    read_offer_total_visits,
    read_or_create_visitor_id,
    read_route_language,
    total_amount_by_payment_type,
    visit_already_recorded_for_session
} from './utils/offer_preview';

const OfferPreview = styled(PageFull)`
    min-height: calc(100vh - 6rem);
    display: grid;
    justify-items: center;
    background: ${p => p.theme.low};
`;

const OfferPreviewContent = styled.div`
    width: min(100%, 90rem);
    padding-bottom: 4rem;
`;

const OfferPreviewPrintLayouts = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 1.6rem;

    @media (max-width: 48rem) {
        grid-template-columns: minmax(0, 1fr);
    }

    @media print {
        display: block;
    }
`;

const OfferPreviewDocument = styled.div`
    min-width: 0;
    display: grid;
    gap: 1.2rem;
`;

export default function OFFER_PREVIEW() {
    const { t } = useTranslation();
    const { offerId, lang } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user.user || {});
    const { isReady: isHrReady, isLoading: isHrLoading, permissions } = useHrAccess();
    const [offerDraft, setOfferDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
    const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
    const [bankTransferPaymentType, setBankTransferPaymentType] = useState('');
    const [adminActionType, setAdminActionType] = useState('');
    const [adminActionError, setAdminActionError] = useState('');
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [isPaidSliderOpen, setIsPaidSliderOpen] = useState(false);
    const [paidRecording, dispatchPaidRecording] = useReducer(
        offer_payment_recording_reducer,
        initial_offer_payment_recording
    );
    const [extensionDaysValue, setExtensionDaysValue] = useState('');
    const [offerApiMode, setOfferApiMode] = useState('agency');
    const routeLanguage = read_route_language(lang);
    const hasKnightAccess = can_mark_agency_offer_paid(user?.roles);
    const rolesResolved = user?.rolesResolved !== false;
    const hasScoutAccess = checkPermissions('Scout', user.roles || []);
    const viewOnly = (!rolesResolved || hasKnightAccess) && searchParams.get('viewOnly') === '1';
    const paymentOverview = build_payment_overview(offerDraft);
    const offerUnavailable = offer_is_unavailable(offerDraft);
    const canViewInternalUi = !offerDraft?.migration && can_view_offer_preview_internal_ui(user?.roles);
    const hasAcceptedTeamMembership = permissions.hasAcceptedTeamMembership === true;
    const fakeOffer = offerDraft?.fakeOffer === true || offerDraft?.setupData?.fakeOffer === true;
    const canUsePaymentActions = !fakeOffer && !['replaced', 'stopped'].includes(offerDraft?.setupData?.serviceActivation?.state) && !offerDraft?.migration
        && !viewOnly;
    const canMarkAgencyOfferPaid = hasAcceptedTeamMembership
        && offerApiMode === 'agency'
        && hasKnightAccess;
    const canSeeHiddenMessages = canViewInternalUi;
    const initialisedPaymentTypes = initialised_payment_types_from_offer(offerDraft || {}).filter((paymentType) => {
        return paymentType !== 'recurring'
            || !paymentOverview.recurringPaymentLinkUrl.length
            || paymentOverview.recurringUsesBankTransfer;
    });
    const paidPaymentType = initialisedPaymentTypes.find((paymentType) => {
        return payment_recording_context_from_offer(offerDraft || {}, paymentType).fulfillRemainingPayment;
    }) || initialisedPaymentTypes[0] || '';
    const offerTotalVisits = read_offer_total_visits(offerDraft || {});
    const canShowPaidButton = !fakeOffer && hasAcceptedTeamMembership && initialisedPaymentTypes.length > 0 && (
        canMarkAgencyOfferPaid
        || (!viewOnly && offerApiMode === 'legacy' && canViewInternalUi)
    );
    const canShowDeleteButton = !viewOnly
        && canViewInternalUi
        && offerTotalVisits === 0
        && Boolean(offerDraft?.offerId);
    const canShowExtendButton = !viewOnly
        && canViewInternalUi
        && offer_can_extend(offerDraft)
        && Boolean(offerDraft?.offerId);
    const canShowAdminActions = canShowExtendButton || canShowPaidButton || canShowDeleteButton;
    const offerCode = offer_reference_from_offer(offerDraft);
    const bankTransferAmount = total_amount_by_payment_type(offerDraft, bankTransferPaymentType);
    const paidPaymentContext = payment_recording_context_from_offer(offerDraft || {}, paidPaymentType);
    const fulfillingPayment = paidPaymentContext.fulfillRemainingPayment;
    const paymentTypeTotal = total_amount_by_payment_type(offerDraft, paidPaymentType);
    const paidPaymentTotal = fulfillingPayment && paidPaymentContext.totalAmount > 0
        ? paidPaymentContext.totalAmount
        : paymentTypeTotal;
    const paidPartialAmountValue = Number(paidRecording.partialAmount || 0);
    const paidPartialAmountIsValid = Number.isFinite(paidPartialAmountValue)
        && paidPartialAmountValue > 0
        && paidPartialAmountValue < paidPaymentTotal;
    const paidPaymentRemaining = fulfillingPayment
        ? paidPaymentContext.remainingAmount
        : Math.max(
            paidPaymentTotal - (Number.isFinite(paidPartialAmountValue) ? paidPartialAmountValue : 0),
            0
        );
    const paymentCurrency = offerDraft?.priceData?.currency || 'EUR';
    const previewCopy = offerPreviewCopyByLanguage[routeLanguage] || offerPreviewCopyByLanguage.el;
    const cerebroConnectionMessage = canViewInternalUi
        ? read_cerebro_connection_message(offerDraft, previewCopy)
        : '';
    const onlineOfferUrl = typeof window === 'undefined'
        ? `/${routeLanguage}/offer/${offerId}`
        : `${window.location.origin}/${routeLanguage}/offer/${offerId}`;

    useEffect(() => {
        if (!rolesResolved || hasKnightAccess || !searchParams.has('viewOnly')) {
            return;
        }

        const paymentSearchParams = new URLSearchParams(searchParams);
        paymentSearchParams.delete('viewOnly');
        navigate({
            pathname: location.pathname,
            search: paymentSearchParams.toString(),
            hash: location.hash
        }, { replace: true });
    }, [rolesResolved, hasKnightAccess, searchParams, location.pathname, location.hash, navigate]);

    useEffect(() => {
        let mounted = true;
        setTermsAccepted(false);
        setPaymentErrorMessage('');
        setBankTransferPaymentType('');
        setIsPaidSliderOpen(false);
        dispatchPaidRecording({ type: 'reset' });

        const get_offer = async() => {
            setIsLoading(true);
            setOfferDraft(null);
            setErrorMessage('');

            try {
                let response = null;
                let apiMode = 'agency';

                try {
                    response = await get_agency_public_offer_by_id(offerId);
                } catch (error) {
                    if ([401, 403].includes(error.response?.status)) throw error;
                    response = await get_public_offer_by_id(offerId);
                    apiMode = 'legacy';
                }

                if (!mounted) {
                    return;
                }

                if (!can_view_public_offer(response?.data?.data, user.roles || [])) { navigate(`/${routeLanguage}/access-denied`, { replace: true }); return; }
                if (response?.data?.data?.schemaVersion === 3 && !response.data.data.migration) { navigate(`/${routeLanguage}/offers/v3/${offerId}/public`, { replace: true }); return; }
                setOfferApiMode(apiMode);
                setOfferDraft(response?.data?.data || null);
            } catch (error) {
                if (!mounted) {
                    return;
                }

                if ([401, 403].includes(error.response?.status)) { navigate(`/${routeLanguage}/access-denied`, { replace: true }); return; }
                setErrorMessage(offerPreviewCopyByLanguage[routeLanguage]?.offerNotFound || offerPreviewCopyByLanguage.el.offerNotFound);
                setOfferDraft(null);
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        get_offer();

        return () => {
            mounted = false;
        };
    }, [offerId, routeLanguage, t, hasScoutAccess, navigate]);

    useEffect(() => {
        if (!offerDraft?.offerId || isLoading || errorMessage.length || !rolesResolved || !isHrReady || isHrLoading) {
            return;
        }

        if (hasAcceptedTeamMembership || fakeOffer) {
            return;
        }

        if (visit_already_recorded_for_session(offerDraft.offerId)) {
            return;
        }

        const visitorId = read_or_create_visitor_id();
        let mounted = true;

        const track_visit = async() => {
            try {
                const trackVisitRequest = offerApiMode === 'legacy'
                    ? track_offer_visit_by_id
                    : track_agency_offer_visit_by_id;

                await trackVisitRequest(offerDraft.offerId, {
                    visitorId
                });

                if (!mounted) {
                    return;
                }

                mark_visit_recorded_for_session(offerDraft.offerId);
            } catch (error) {
            }
        };

        track_visit();

        return () => {
            mounted = false;
        };
    }, [
        offerDraft?.offerId,
        isLoading,
        errorMessage,
        rolesResolved,
        isHrReady,
        isHrLoading,
        hasAcceptedTeamMembership,
        fakeOffer,
        offerApiMode
    ]);

    const proceed_to_payment = async(paymentType) => {
        if (!termsAccepted || isInitiatingPayment || !offerDraft?.offerId || offerUnavailable) {
            return;
        }

        const paymentTypeValue = paymentType === 'one_off' ? 'one_off' : 'recurring';

        setPaymentErrorMessage('');
        setIsInitiatingPayment(true);

        try {
            const initPaymentRequest = offerApiMode === 'legacy'
                ? init_offer_payment_by_id
                : init_agency_offer_payment_by_id;

            await initPaymentRequest(offerDraft.offerId, paymentTypeValue);

            if (paymentTypeValue === 'recurring') {
                if (paymentOverview.recurringUsesBankTransfer) {
                    setBankTransferPaymentType(paymentTypeValue);
                    return;
                }

                setPaymentErrorMessage(previewCopy.paymentLinkMissing);
                return;
            }

            if (paymentOverview.oneOffUsesBankTransfer) {
                setBankTransferPaymentType(paymentTypeValue);
                return;
            }

            setPaymentErrorMessage(previewCopy.paymentLinkMissing);
        } catch (error) {
            setPaymentErrorMessage(previewCopy.paymentStartFailed);
        } finally {
            setIsInitiatingPayment(false);
        }
    };

    const proceed_to_payment_link = (event, paymentType) => {
        if (!termsAccepted || !offerDraft?.offerId || isInitiatingPayment || offerUnavailable) {
            event.preventDefault();
            return;
        }

        const paymentTypeValue = paymentType === 'one_off' ? 'one_off' : 'recurring';

        setPaymentErrorMessage('');
        setBankTransferPaymentType('');
        setIsInitiatingPayment(true);

        const initPaymentRequest = offerApiMode === 'legacy'
            ? init_offer_payment_by_id(offerDraft.offerId, paymentTypeValue)
            : init_agency_offer_payment_by_id(offerDraft.offerId, paymentTypeValue);

        initPaymentRequest
            .catch(() => {
                setPaymentErrorMessage(previewCopy.paymentStartFailed);
            })
            .finally(() => {
                setIsInitiatingPayment(false);
            });
    };

    const print_with_mode = () => {
        window.setTimeout(() => {
            window.print();
        }, 40);
    };

    const export_offer_pdf = () => {
        print_with_mode();
    };

    const change_extension_days = (event) => {
        setExtensionDaysValue(event.target.value);
        setAdminActionError('');
    };

    const change_terms_acceptance = (event) => {
        setTermsAccepted(event.target.checked);
        setPaymentErrorMessage('');
    };

    const open_paid_slider = () => {
        if (!canShowPaidButton || adminActionType.length) {
            return;
        }

        setAdminActionError('');
        dispatchPaidRecording({ type: 'reset' });
        setIsPaidSliderOpen(true);
    };

    const close_paid_slider = () => {
        if (adminActionType === 'paid') {
            return;
        }

        setAdminActionError('');
        dispatchPaidRecording({ type: 'reset' });
        setIsPaidSliderOpen(false);
    };

    const toggle_paid_register_prepayment = () => {
        if (adminActionType === 'paid') {
            return;
        }

        dispatchPaidRecording({ type: 'toggle-register-prepayment' });
        setAdminActionError('');
    };

    const change_paid_partial_amount = (event) => {
        dispatchPaidRecording({
            type: 'change-prepayment',
            value: event.target.value
        });
        setAdminActionError('');
    };

    const extend_offer_validity = async() => {
        if (!canShowExtendButton || adminActionType.length || !offerDraft?.offerId) {
            return;
        }

        const extraDays = Math.floor(Number(extensionDaysValue || 0));

        if (!Number.isFinite(extraDays) || extraDays <= 0) {
            setAdminActionError(previewCopy.invalidExtendDays);
            return;
        }

        setAdminActionError('');
        setAdminActionType('extend');

        try {
            const extendOfferRequest = offerApiMode === 'legacy'
                ? extend_offer_validity_by_id
                : extend_agency_offer_validity_by_id;
            const response = await extendOfferRequest(offerDraft.offerId, {
                extraDays,
                extendedBy: {
                    id: user?.id || user?.userId || user?.subId || '',
                    username: user?.username || user?.preferred_username || '',
                    email: user?.email || ''
                }
            });
            const updatedOffer = response?.data?.data || null;

            if (!updatedOffer) {
                setAdminActionError(previewCopy.extendOfferFailed);
                return;
            }

            setOfferDraft(updatedOffer);
            setExtensionDaysValue('');
        } catch (error) {
            setAdminActionError(previewCopy.extendOfferFailed);
        } finally {
            setAdminActionType('');
        }
    };

    const mark_initialised_offer_paid = async() => {
        if (!canShowPaidButton || adminActionType.length || !offerDraft?.offerId) {
            return;
        }

        if (!fulfillingPayment && paidRecording.registerPrepayment && !paidPartialAmountIsValid) {
            setAdminActionError(previewCopy.partialAmountRequired);
            return;
        }

        const confirmationData = build_payment_confirmation({
            ...paidRecording,
            fulfillRemainingPayment: fulfillingPayment
        });

        setAdminActionError('');
        setAdminActionType('paid');

        try {
            const confirmPaymentRequest = offerApiMode === 'legacy'
                ? confirm_offer_payment_by_id
                : confirm_agency_offer_payment_by_id;
            const response = await confirmPaymentRequest(offerDraft.offerId, paidPaymentType, '', confirmationData);
            const updatedOffer = response?.data?.data || null;

            if (!updatedOffer) {
                setAdminActionError(previewCopy.paidStatusFailed);
                return;
            }

            setOfferDraft(updatedOffer);
            setIsPaidSliderOpen(false);
            dispatchPaidRecording({ type: 'reset' });
        } catch (error) {
            setAdminActionError(previewCopy.paidStatusFailed);
        } finally {
            setAdminActionType('');
        }
    };

    const delete_current_offer = async() => {
        if (!canShowDeleteButton || adminActionType.length || !offerDraft?.offerId) {
            return;
        }

        setAdminActionError('');
        setAdminActionType('delete');

        try {
            const deleteOfferRequest = offerApiMode === 'legacy'
                ? delete_offer_by_id
                : delete_agency_offer_by_id;

            await deleteOfferRequest(offerDraft.offerId);
            setIsDeleteConfirmationOpen(false);
            navigate(`/${routeLanguage}`);
        } catch (error) {
            setAdminActionError(previewCopy.deleteOfferFailed);
        } finally {
            setAdminActionType('');
        }
    };

    return (
        <OfferPreview>
            <OfferPreviewContent>
                {isPaidSliderOpen && canShowPaidButton ? (
                    <Offer_Preview_Paid_Slider
                        copy={previewCopy}
                        fulfillRemainingPayment={fulfillingPayment}
                        registerPrepayment={fulfillingPayment ? false : paidRecording.registerPrepayment}
                        partialAmount={paidRecording.partialAmount}
                        partialAmountIsValid={paidPartialAmountIsValid}
                        paymentTotal={paidPaymentTotal}
                        paymentRemaining={paidPaymentRemaining}
                        currency={paymentCurrency}
                        isSaving={adminActionType === 'paid'}
                        errorMessage={adminActionError}
                        onRegisterPrepaymentToggle={toggle_paid_register_prepayment}
                        onPartialAmountChange={change_paid_partial_amount}
                        onClose={close_paid_slider}
                        onConfirm={mark_initialised_offer_paid}
                    ></Offer_Preview_Paid_Slider>
                ) : null}
                {!viewOnly && isDeleteConfirmationOpen && canShowDeleteButton ? (
                    <Confirmation_
                        message={previewCopy.deleteOfferQuestion}
                        no={previewCopy.no}
                        yes={adminActionType === 'delete' ? previewCopy.deleting : previewCopy.yes}
                        onNo={() => setIsDeleteConfirmationOpen(false)}
                        onYes={delete_current_offer}
                    ></Confirmation_>
                ) : null}
                {canUsePaymentActions && bankTransferPaymentType ? (
                    <Offer_Bank_Transfer
                        t={t}
                        copy={previewCopy}
                        offerCode={offerCode}
                        amount={bankTransferAmount}
                        onClose={() => setBankTransferPaymentType('')}
                    ></Offer_Bank_Transfer>
                ) : null}
                {adminActionError && !isPaidSliderOpen ? <p>{adminActionError}</p> : null}
                {cerebroConnectionMessage ? <p>{cerebroConnectionMessage}</p> : null}
                {isLoading ? <p>{previewCopy.loadingOffer}</p> : null}
                {errorMessage ? <p>{errorMessage}</p> : null}
                {!isLoading && !errorMessage && offerDraft ? (
                    <OfferPreviewPrintLayouts>
                        <OfferPreviewDocument>
                            <Offer_Preview_Export_Action
                                copy={previewCopy}
                                onExportPdf={export_offer_pdf}
                            ></Offer_Preview_Export_Action>
                            <New_Offer_Preview_Page
                                offerDraft={offerDraft}
                                hideActions
                                showInternalNotes={canSeeHiddenMessages}
                                onlineOfferUrl={onlineOfferUrl}
                            ></New_Offer_Preview_Page>
                            {canUsePaymentActions ? (
                                <Offer_Preview_Payment
                                    copy={previewCopy}
                                    offerUnavailable={offerUnavailable}
                                    termsAccepted={termsAccepted}
                                    paymentOverview={paymentOverview}
                                    isInitiating={isInitiatingPayment}
                                    errorMessage={paymentErrorMessage}
                                    onTermsChange={change_terms_acceptance}
                                    onProceed={proceed_to_payment}
                                    onProceedToLink={proceed_to_payment_link}
                                ></Offer_Preview_Payment>
                            ) : null}
                        </OfferPreviewDocument>
                        {canShowAdminActions ? (
                            <Offer_Preview_Admin_Actions
                                copy={previewCopy}
                                actionType={adminActionType}
                                canExtend={canShowExtendButton}
                                canMarkPaid={canShowPaidButton}
                                canDelete={canShowDeleteButton}
                                paymentActionText={fulfillingPayment ? previewCopy.fulfillPayment : previewCopy.paid}
                                extensionDays={extensionDaysValue}
                                onExtensionDaysChange={change_extension_days}
                                onExtend={extend_offer_validity}
                                onMarkPaid={open_paid_slider}
                                onDelete={() => setIsDeleteConfirmationOpen(true)}
                            ></Offer_Preview_Admin_Actions>
                        ) : null}
                    </OfferPreviewPrintLayouts>
                ) : null}
            </OfferPreviewContent>
        </OfferPreview>
    );
}
